import {
  BudgetRaise,
  BudgetSettings,
  NotificationList,
  ProjectBudget,
  type DecideRaiseRequest,
  type RequestRaiseRequest,
  type SetBudgetRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

/*
 * Budgets and notifications (docs/budgets.md). A project's budget lives
 * under its `projects` key (with its costs); raise requests of the
 * organisation and the settings under `budgets`; notifications under
 * `notifications`. `budget.changed` and `notification.created` refresh them.
 */

export const budgetKeys = {
  project: (projectId: string) => ["projects", projectId, "budget"] as const,
  raises: ["budgets", "raises"] as const,
  settings: ["budgets", "settings"] as const,
  notifications: ["notifications"] as const,
};

export const useProjectBudget = (projectId: string) =>
  useQuery({
    queryKey: budgetKeys.project(projectId),
    queryFn: () => api(`/projects/${projectId}/budget`, { schema: ProjectBudget }),
  });

/** Pending raise requests of the organisation (administrators). */
export const usePendingRaises = (enabled: boolean) =>
  useQuery({
    queryKey: budgetKeys.raises,
    queryFn: () => api("/budget-raises?status=pending", { schema: z.array(BudgetRaise) }),
    enabled,
  });

export const useBudgetSettings = (enabled: boolean) =>
  useQuery({
    queryKey: budgetKeys.settings,
    queryFn: () => api("/budgets/settings", { schema: BudgetSettings }),
    enabled,
  });

/** Budget writes change what costs, budgets and raise lists show. */
function useBudgetMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["projects"] });
      await client.invalidateQueries({ queryKey: ["budgets"] });
      await client.invalidateQueries({ queryKey: ["costs"] });
    },
  });
}

export const useSetBudget = (projectId: string) =>
  useBudgetMutation((body: SetBudgetRequest) =>
    api(`/projects/${projectId}/budget`, { method: "PUT", body }),
  );

export const useRequestRaise = (projectId: string) =>
  useBudgetMutation((body: RequestRaiseRequest) =>
    api(`/projects/${projectId}/budget/raises`, { method: "POST", body, schema: BudgetRaise }),
  );

export const useDecideRaise = () =>
  useBudgetMutation(
    ({
      id,
      decision,
      ...body
    }: DecideRaiseRequest & { id: string; decision: "approve" | "reject" }) =>
      api(`/budget-raises/${id}/${decision}`, { method: "POST", body, schema: BudgetRaise }),
  );

export const useSetBudgetSettings = () =>
  useBudgetMutation((defaultChangeMargin: number) =>
    api("/budgets/settings", {
      method: "PUT",
      body: { defaultChangeMargin },
      schema: BudgetSettings,
    }),
  );

export const useNotifications = (enabled = true) =>
  useQuery({
    queryKey: budgetKeys.notifications,
    queryFn: () => api("/notifications", { schema: NotificationList }),
    enabled,
  });

export function useMarkRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      api("/notifications/read", { method: "POST", body: ids ? { ids } : {} }),
    onSuccess: () => client.invalidateQueries({ queryKey: budgetKeys.notifications }),
  });
}
